'use client';

import { useRef } from 'react';
import { useServerInsertedHTML } from 'next/navigation';

// Themes applied before first paint, so a dark preference doesn't flash the
// light pages:
//
// * Marketing (visitor) theme. Mirrors hooks/useMarketingTheme: the
//   `mf_marketing_theme` cookie (Max-Age 24h; value is the cookie store's
//   URI-encoded JSON, hence %22) holds light/dark; when absent the OS scheme
//   decides. Dark ⇒ `.marketing-dark` on <html>.
// * App theme, on app routes only (/spaces, /settings, /notifications).
//   Mirrors hooks/useTheme: the `theme` localStorage key holds JSON
//   'light' | 'dark' | 'system'. Dark ⇒ `.dark` on <html>, plus the dark
//   canvas colour on <html> itself so the overscroll area matches. SiteChrome
//   takes over both after hydration.
const THEME_SCRIPT = `(function(){try{var h=document.documentElement;var m=document.cookie.match(/(?:^|; ?)mf_marketing_theme=%22(light|dark)%22/);var os=matchMedia('(prefers-color-scheme: dark)').matches;if(m?m[1]==='dark':os)h.classList.add('marketing-dark');if(['spaces','settings','notifications'].indexOf(location.pathname.split('/')[1])>-1){var t=JSON.parse(localStorage.getItem('theme')||'"system"');if(t==='dark'||(t==='system'&&os)){h.classList.add('dark');h.style.backgroundColor='#14161c'}}}catch(e){}})()`;

// Injected with useServerInsertedHTML so the script exists only in the
// streamed initial document (where the parser executes it pre-paint).
// Rendering a <script> through React instead would re-create it on client
// navigations — the [locale] layout remounts when the locale changes — and
// React warns that client-rendered scripts never execute. On the client this
// hook is a no-op, and the theme class is already on <html> anyway (kept up
// to date by useMarketingTheme).
export function MarketingThemeScript() {
  const inserted = useRef(false);
  useServerInsertedHTML(() => {
    // Guard against multiple flushes inserting duplicates.
    if (inserted.current) return null;
    inserted.current = true;
    return <script dangerouslySetInnerHTML={{ __html: THEME_SCRIPT }} />;
  });
  return null;
}
